import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentWidth, PageSizing } from 'components/Layout/PageContentWidth';
import {
  StyledPaginationWrapper,
  StyledSearchInput,
  StyledTableScrollContainer,
  StyledTableToolbar,
  StyledTableWrapper,
} from './styled';
import Input from '__components/Kizen/Input';
import { KizenTypography } from '__app/typography';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import BasicTable from '__components/Tables/Basic';
import { TRow } from '__components/Kizen/Table';
import { usePaginationSettings } from '__hooks/usePaginationSettings';
import TablePagination from '__components/Tables/Big/TablePagination';
import { useColumns } from 'components/Modals/HistoryModal/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ActivityTimelineEntry } from '@kizen/api/activities';
import { activityHistoryQuery } from 'query/activities';
import { HistoryDetailsModal } from 'components/Modals/HistoryModal/HistoryDetailsModal';

type TimelinePageProps = {
  activityObjectId: string;
};

const paginationSettings = {
  page: 1,
  pageSize: 10,
  ordering: '-created',
};

export const TimelinePage: FC<TimelinePageProps> = ({ activityObjectId }) => {
  const { t } = useTranslation();

  const {
    search,
    actualSearch,
    handleChangeSearch,
    page,
    handleChangePage,
    pageSize,
    handleChangePageSize,
    ordering,
    headData,
  } = usePaginationSettings(paginationSettings);

  const [timelineEvent, setTimelineEvent] =
    useState<ActivityTimelineEntry | null>(null);

  const { data, isLoading } = useQuery(
    activityHistoryQuery(activityObjectId, {
      params: {
        page,
        page_size: pageSize,
        ordering,
        search,
      },
      placeholderData: keepPreviousData,
      staleTime: 0,
    })
  );

  const handleSelectAction = useCallback(
    ({ value }: { value: 'view' }, data: ActivityTimelineEntry) => {
      if (value === 'view') {
        setTimelineEvent(data);
      }
    },
    []
  );

  const handleHide = useCallback(() => {
    setTimelineEvent(null);
  }, []);

  const columns = useColumns(handleSelectAction);

  return (
    <PageSizing>
      <ContentWidth>
        <StyledTableToolbar>
          <StyledSearchInput
            pill
            type={Input.INPUT_TYPES.SEARCH}
            placeholder={t('Find Activities')}
            value={actualSearch}
            onChange={handleChangeSearch}
          />
          <KizenTypography as="h2" type="header">
            {t('Activity History Log')}
          </KizenTypography>
          <div />
        </StyledTableToolbar>
      </ContentWidth>
      <ContentWidth>
        <StyledTableWrapper>
          <StyledTableScrollContainer
            isTable
            bottom
            refresh={isLoading}
            top
            gradient={<Gradient />}
          >
            <BasicTable
              stickyHeader
              head={<TRow head columns={columns} data={headData} />}
              data-qa="history-table"
              staleData={isLoading}
            >
              {data?.count ? null : <TRow />}
              {(data?.results || []).map((data) => (
                <TRow key={data.id} columns={columns} data={data} />
              ))}
            </BasicTable>
          </StyledTableScrollContainer>
        </StyledTableWrapper>
        <StyledPaginationWrapper>
          <TablePagination
            page={page}
            perPage={pageSize}
            totalCount={data?.count || 0}
            onChangePage={handleChangePage}
            onChangePerPage={handleChangePageSize}
          />
        </StyledPaginationWrapper>
      </ContentWidth>
      {timelineEvent && (
        <HistoryDetailsModal data={timelineEvent.data} onHide={handleHide} />
      )}
    </PageSizing>
  );
};
